import { Component, input, InputSignal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { Payout, PayoutStatus } from 'src/app/models/payout.model';
import { PayoutsStoreService } from 'src/app/services/stores/payouts.store.service';

@Component({
  selector: 'app-admin-payouts-table',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatButtonModule, MatChipsModule],
  templateUrl: './admin-payouts-table.component.html',
  styleUrl: './admin-payouts-table.component.scss',
})
export class AdminPayoutsTableComponent {
  private _payoutsStoreService = inject(PayoutsStoreService);

  payouts: InputSignal<Payout[]> = input.required<Payout[]>();
  displayedColumns: string[] = [
    'userEmail',
    'paymentName',
    'paymentEmail',
    'amount',
    'status',
    'createdAt',
    'updatedAt',
    'actions',
  ];

  protected getStatusLabel(status: PayoutStatus): string {
    switch (status) {
      case PayoutStatus.REQUESTED:
        return 'Requested';
      case PayoutStatus.SENT_FOR_PAYMENT:
        return 'Sent for Payment';
      case PayoutStatus.PAID:
        return 'Paid';
      case PayoutStatus.CANCELLED:
        return 'Cancelled';
      default:
        return status;
    }
  }

  protected canMoveToNextStatus(status: PayoutStatus): boolean {
    return status !== PayoutStatus.PAID && status !== PayoutStatus.CANCELLED;
  }

  protected getNextStatus(status: PayoutStatus): PayoutStatus | null {
    switch (status) {
      case PayoutStatus.REQUESTED:
        return PayoutStatus.SENT_FOR_PAYMENT;
      case PayoutStatus.SENT_FOR_PAYMENT:
        return PayoutStatus.PAID;
      default:
        return null;
    }
  }

  protected getNextStatusLabel(status: PayoutStatus): string {
    const nextStatus = this.getNextStatus(status);
    if (!nextStatus) return '';
    return this.getStatusLabel(nextStatus);
  }

  protected canCancel(status: PayoutStatus): boolean {
    return (
      status === PayoutStatus.REQUESTED ||
      status === PayoutStatus.SENT_FOR_PAYMENT
    );
  }

  protected async cancelPayout(payout: Payout): Promise<void> {
    if (!payout.id) return;

    try {
      await this._payoutsStoreService.updatePayoutStatus(
        payout.id,
        PayoutStatus.CANCELLED,
      );
    } catch (error) {
      console.error('Error cancelling payout:', error);
    }
  }

  protected async updateStatus(payout: Payout): Promise<void> {
    if (!payout.id) return;

    const nextStatus = this.getNextStatus(payout.status);
    if (!nextStatus) return;

    try {
      await this._payoutsStoreService.updatePayoutStatus(payout.id, nextStatus);
    } catch (error) {
      console.error('Error updating payout status:', error);
    }
  }
}
